import { User, UserCheck, MessageSquare } from 'lucide-react';
import type { ReactNode } from 'react';

// Roles that can appear on a transcript segment (superset of segment union + interpreter)
export type DisplaySpeakerRole =
  | 'physician' | 'patient' | 'nurse' | 'family' | 'interpreter' | 'other';

// Canonical display order for the legend
export const SPEAKER_ROLE_ORDER: DisplaySpeakerRole[] =
  ['physician', 'patient', 'nurse', 'family', 'interpreter', 'other'];

interface RoleConfig {
  label: string;                          // e.g. "Family Member"
  legendTextColor: string;                // tailwind text color for the legend label
  icon: (size: number) => ReactNode;      // lucide icon w/ its color, sized by caller
  bg: (hasEntities: boolean) => string;   // segment background/border classes
}

export const SPEAKER_ROLE_CONFIG: Record<DisplaySpeakerRole, RoleConfig> = {
  physician: {
    label: 'Physician',
    legendTextColor: 'text-blue-700',
    icon: (s) => <UserCheck className="text-blue-600" size={s} />,
    bg: (e) => e
      ? 'bg-blue-50/50 border-blue-200 shadow-sm hover:shadow-md hover:border-blue-300'
      : 'bg-blue-50/30 border-blue-100 hover:bg-blue-50/50 hover:border-blue-200',
  },
  patient: {
    label: 'Patient',
    legendTextColor: 'text-green-700',
    icon: (s) => <User className="text-green-600" size={s} />,
    bg: (e) => e
      ? 'bg-green-50/50 border-green-200 shadow-sm hover:shadow-md hover:border-green-300'
      : 'bg-green-50/30 border-green-100 hover:bg-green-50/50 hover:border-green-200',
  },
  nurse: {
    label: 'Nurse',
    legendTextColor: 'text-purple-700',
    icon: (s) => <UserCheck className="text-purple-600" size={s} />,
    bg: (e) => e
      ? 'bg-purple-50/50 border-purple-200 shadow-sm hover:shadow-md hover:border-purple-300'
      : 'bg-purple-50/30 border-purple-100 hover:bg-purple-50/50 hover:border-purple-200',
  },
  family: {
    label: 'Family Member',
    legendTextColor: 'text-orange-700',
    icon: (s) => <User className="text-orange-600" size={s} />,
    bg: (e) => e
      ? 'bg-orange-50/50 border-orange-200 shadow-sm hover:shadow-md hover:border-orange-300'
      : 'bg-orange-50/30 border-orange-100 hover:bg-orange-50/50 hover:border-orange-200',
  },
  interpreter: {
    label: 'Interpreter',
    legendTextColor: 'text-teal-700',
    icon: (s) => <User className="text-teal-600" size={s} />,
    bg: (e) => e
      ? 'bg-teal-50/50 border-teal-200 shadow-sm hover:shadow-md hover:border-teal-300'
      : 'bg-teal-50/30 border-teal-100 hover:bg-teal-50/50 hover:border-teal-200',
  },
  other: {
    label: 'Unknown Speaker',
    legendTextColor: 'text-gray-600',
    icon: (s) => <MessageSquare className="text-gray-500" size={s} />,
    bg: (e) => e
      ? 'bg-white border-gray-200 shadow-sm hover:shadow-md hover:border-blue-200'
      : 'bg-gray-50/50 border-gray-100 hover:bg-white hover:border-gray-200',
  },
};

// Normalize a raw segment role (incl. 'doctor' alias and legacy free-text) to a config key.
export function normalizeSpeakerRole(raw?: string): DisplaySpeakerRole {
  switch (raw) {
    case 'physician':
    case 'doctor':      return 'physician';
    case 'patient':     return 'patient';
    case 'nurse':       return 'nurse';
    case 'family':      return 'family';
    case 'interpreter': return 'interpreter';
    default:            return 'other';
  }
}
